import React from 'react'
import { Field, useFormikContext } from 'formik'
import Upload from 'components/Ui/Upload'
import { HiPaperClip, HiDownload } from 'react-icons/hi'
import { downloadInvoiceAttachment } from 'services/XeroService'
import { Success, Error } from 'utils/alerts'

const AttachDocuments = ({ recordData, xeroConfig }) => {
    const { values, errors, touched, setFieldValue } = useFormikContext()
    // Get existing attachments from recordData
    const existingAttachments = recordData?.attachments || []

    const visibleAttachments = existingAttachments

    const formatFileSize = bytes => {
        if (!bytes) return 'Unknown size'
        return (bytes / 1024 / 1024).toFixed(2) + ' MB'
    }

    const handleDownloadAttachment = async attachment => {
        try {
            if (!xeroConfig || !xeroConfig.id) {
                Error('No Xero configuration available for download')
                return
            }

            await downloadInvoiceAttachment(xeroConfig.id, recordData.invoice_id, attachment.attachment_id, attachment.filename)
            Success('File downloaded successfully!')
        } catch (error) {
            console.error('Download error:', error)
            Error('Failed to download file. Please try again.')
        }
    }

    return (
        <div className="space-y-6">
            <h4 className="text-lg font-medium">Document Attachments</h4>

            {/* Existing Attachments Section */}
            {visibleAttachments.length > 0 && (
                <div className="space-y-4">
                    <h5 className="text-md font-medium text-gray-900">Existing Attachments</h5>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-3">
                            {visibleAttachments.map(attachment => (
                                <div
                                    key={attachment.id}
                                    className="flex items-center justify-between p-3 bg-white rounded border"
                                >
                                    <div className="flex items-center space-x-3">
                                        <HiPaperClip className="h-5 w-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                                            <p className="text-xs text-gray-500">
                                                {formatFileSize(attachment.file_size)} • {attachment.mime_type}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleDownloadAttachment(attachment)}
                                        className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                        title="Download attachment"
                                    >
                                        <HiDownload className="h-4 w-4" />
                                        <span>Download</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Add New Attachments Section */}
            <div className="space-y-4">
                <h5 className="text-md font-medium text-gray-900">Add New Attachments</h5>
                <div className="p-4 bg-blue-50 rounded">
                    <p className="text-sm text-blue-800 mb-2">
                        <strong>Note:</strong> New documents will be attached to the invoice after it's saved in Xero.
                    </p>
                    <ul className="text-xs text-blue-700 space-y-1">
                        <li>• Maximum file size: 4MB per file</li>
                        <li>• Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF</li>
                        <li>• Maximum 10 attachments per invoice</li>
                    </ul>
                </div>

                <Field name="files">
                    {({ field, form }) => (
                        <Upload
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
                            onFilesSelected={files => {
                                form.setFieldValue(field.name, files)
                            }}
                        />
                    )}
                </Field>

                {values.files?.length > 0 && (
                    <div className="mt-4">
                        <h6 className="text-sm font-medium mb-2">Selected New Files:</h6>
                        <ul className="space-y-1">
                            {values.files.map((file, index) => (
                                <li key={index} className="text-sm text-gray-600 flex items-center space-x-2">
                                    <HiPaperClip className="h-4 w-4 text-gray-400" />
                                    <span>
                                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AttachDocuments
